/**
 * Navigation persistence for Auto Sourcing.
 *
 * The customer's active scan must stay findable when they move between Search,
 * Results, Saved Profiles and History. React state alone is not enough: leaving
 * the page unmounts it, so the tab, the job the customer was watching, and the
 * Results filters all reset — and worse, the "restore" path re-derived a job from
 * "newest run", which could silently show a DIFFERENT scan.
 *
 * Everything here is pure except the two guarded storage wrappers, so the
 * restoration rules can be tested without a browser.
 */

export const SCAN_STATE_KEY = "izisell.autoSourcing.v1";

export const TERMINAL_STATUSES = ["completed", "partial", "failed", "cancelled", "budget_exhausted"];

export function isTerminalStatus(status) {
  return TERMINAL_STATUSES.includes(String(status || "").toLowerCase());
}

function safeGet(storage) {
  try {
    return storage?.getItem?.(SCAN_STATE_KEY) || null;
  } catch {
    return null;
  }
}

function safeSet(storage, value) {
  try {
    storage?.setItem?.(SCAN_STATE_KEY, value);
  } catch {
    /* private mode / quota — persistence is a convenience, never a hard failure */
  }
}

/** Read the persisted scan state. Always returns an object. */
export function readScanState(storage = globalThis?.localStorage) {
  const raw = safeGet(storage);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

/** Merge a partial patch into the persisted scan state. */
export function writeScanState(patch, storage = globalThis?.localStorage) {
  const next = { ...readScanState(storage), ...(patch || {}) };
  safeSet(storage, JSON.stringify(next));
  return next;
}

function createdMs(run) {
  const t = new Date(run?.created_at || run?.queued_at || 0).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Which job should the Results view restore?
 *
 * Rules, in priority order:
 *  1. A persisted job id that still exists wins — even if it has finished. The
 *     customer was watching THAT scan; silently swapping in a newer one is the
 *     defect this exists to prevent.
 *  2. Otherwise the newest non-terminal job (a real scan is in flight).
 *  3. Otherwise the newest job.
 *
 * `source` is returned for tests and diagnostics, never shown to the customer.
 */
export function resolveRestoredRun(runs, persistedRunId) {
  const items = Array.isArray(runs) ? runs.filter((r) => r && r.run_id) : [];
  if (!items.length) return { run: null, source: "none" };

  if (persistedRunId) {
    const found = items.find((r) => String(r.run_id) === String(persistedRunId));
    if (found) return { run: found, source: "persisted" };
  }

  const active = items.filter((r) => !isTerminalStatus(r.status));
  if (active.length) {
    const newest = active.slice().sort((a, b) => createdMs(b) - createdMs(a))[0];
    return { run: newest, source: "newest_active" };
  }

  const newest = items.slice().sort((a, b) => createdMs(b) - createdMs(a))[0];
  return { run: newest, source: "newest" };
}

/**
 * Multiple concurrent jobs must be an explicit choice, not a silent pick.
 *
 * Returns the in-flight jobs (newest first) plus the one to focus by default.
 */
export function activeRunChoices(runs, focusedRunId = null) {
  const items = Array.isArray(runs) ? runs.filter((r) => r && r.run_id) : [];
  const active = items
    .filter((r) => !isTerminalStatus(r.status))
    .sort((a, b) => createdMs(b) - createdMs(a));
  const focusedId = focusedRunId ? String(focusedRunId) : null;
  const focused = focusedId ? active.find((r) => String(r.run_id) === focusedId) : null;
  return {
    jobs: active,
    focused: focused || active[0] || null,
    needsChoice: active.length > 1,
  };
}

// ── Results view filters, scoped per job ────────────────────────────────────

const FILTER_KEY_PREFIX = "izisell.autoSourcing.filters.";

export function filtersKey(runId) {
  return `${FILTER_KEY_PREFIX}${runId || "none"}`;
}

/**
 * Persisted Results filters for one job.
 *
 * Scoped per run on purpose: a filter chosen for job A must never be applied to
 * job B, and returning to job A must restore exactly what was on screen.
 */
export function readFilters(runId, storage = globalThis?.sessionStorage) {
  if (!runId) return {};
  try {
    const raw = storage?.getItem?.(filtersKey(runId));
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function writeFilters(runId, patch, storage = globalThis?.sessionStorage) {
  if (!runId) return {};
  const next = { ...readFilters(runId, storage), ...(patch || {}) };
  try {
    storage?.setItem?.(filtersKey(runId), JSON.stringify(next));
  } catch {
    /* ignore */
  }
  return next;
}
